import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Banknote, IndianRupee, Camera, Loader2 } from "lucide-react";

import { useTranslation } from "react-i18next";
const rupee = (v) => "₹" + Math.round(Number(v) || 0).toLocaleString("en-IN");

/**
 * CSA-initiated mid-shift cash handover to the manager.
 * Distinct from the FINAL shift-close submission which happens on HISAB submit.
 * Each handover is stored `pending` until the manager confirms in their approvals inbox.
 */
export default function CashHandoverDialog({ open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [photoB64, setPhotoB64] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState({ items: [], total: 0 });

  useEffect(() => {
    if (!open) return;
    api.get("/csa/cash-handover/mine")
      .then((r) => setHistory(r.data || { items: [], total: 0 }))
      .catch(() => {});
  }, [open]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error(t("components.cashhandoverdialog.photo_too_large_max_15_mb"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhotoB64(String(reader.result));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error(t("components.cashhandoverdialog.enter_the_cash_amount"));
    setSaving(true);
    try {
      await api.post("/csa/cash-handover", {
        amount: amt,
        note: note.trim() || null,
        photo_base64: photoB64,
      });
      toast.success(`${rupee(amt)} submitted — awaiting manager confirmation`);
      setAmount(""); setNote(""); setPhotoB64(null);
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="cash-handover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-emerald-600" />
            Submit cash to manager
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-[12px] text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
            This is a <b>mid-shift</b> handover. Your manager will confirm it in their inbox.
            The final HISAB submission at shift close is separate — do <b>not</b> use this for that.
          </div>

          {history.items.length > 0 && (
            <div className="rounded-lg border border-border bg-secondary/40 p-3">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                Handovers this shift · <span className="text-foreground">{rupee(history.total)}</span>
              </div>
              <div className="mt-1.5 space-y-0.5">
                {history.items.slice(0, 4).map((it) => (
                  <div key={it.id} className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">
                      {new Date(it.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {it.status === "pending" && " · pending"}
                      {it.manager_name && ` · confirmed by ${it.manager_name}`}
                    </span>
                    <span className="font-mono tabular-nums text-foreground">{rupee(it.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="ho-amount">Amount (₹) <span className="text-red-600">*</span></Label>
            <div className="relative mt-1">
              <IndianRupee className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="ho-amount"
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 5000"
                className="pl-8 font-mono tabular-nums"
                data-testid="cash-handover-amount"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ho-note">{t("csa.csaportal.note_optional")}</Label>
            <Textarea
              id="ho-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Handed to Rakesh at 4:30 PM"
              rows={2}
              data-testid="cash-handover-note"
            />
          </div>

          <div>
            <Label className="text-[11px]">{t("components.cashhandoverdialog.optional_receipt_photo")}</Label>
            <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border bg-background px-3 py-2 text-xs text-muted-foreground hover:border-primary/40">
              <Camera className="h-4 w-4" />
              {photoB64 ? "Photo attached · tap to change" : "Take a photo (optional)"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="cash-handover-photo" />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving || !amount} data-testid="cash-handover-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
            Submit ₹{amount || "0"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
